import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop", "sheet", "title", "message", "confirmLabel"]

  connect() {
    this._isOpen = false
    this._deleteUrl = null
    this._handleEscape = (e) => { if (e.key === "Escape" && this._isOpen) this.close() }
    document.addEventListener("keydown", this._handleEscape)
  }

  disconnect() {
    document.removeEventListener("keydown", this._handleEscape)
  }

  open({ params }) {
    if (this._isOpen) return

    this._deleteUrl = params.url
    this._turboAction = params.turboAction || null
    if (this.hasTitleTarget) this.titleTarget.textContent = params.title || ""
    if (this.hasMessageTarget) this.messageTarget.textContent = params.message || ""
    // Optional per-trigger confirm verb (e.g. "Yes, disconnect"); falls back
    // to the server-rendered default ("Yes, delete") when absent.
    if (this.hasConfirmLabelTarget) {
      if (!this._defaultConfirmLabel) this._defaultConfirmLabel = this.confirmLabelTarget.textContent
      this.confirmLabelTarget.textContent = params.confirmLabel || this._defaultConfirmLabel
    }

    // Delay 180ms so dropdown exit animation (160ms) completes first
    setTimeout(() => this._showSheet(), 180)
  }

  close() {
    if (!this._isOpen) return
    this._isOpen = false

    this._bottomNavBridge?.show()

    // pointer-events-none MUST be added synchronously — if a Turbo morph/refresh
    // happens during the 220ms animation, the old backdrop (without pointer-events-none)
    // could persist and block all touches on iOS.
    this.backdropTarget.style.opacity = ""  // clear any inline opacity from drag
    this.backdropTarget.classList.add("opacity-0", "pointer-events-none")

    this.sheetTarget.style.transition = "transform 220ms cubic-bezier(0.4, 0, 1, 1)"
    this.sheetTarget.style.transform = ""  // clear drag transform so the classes win
    this.sheetTarget.classList.remove("translate-y-0")
    this.sheetTarget.classList.add("translate-y-full")
  }

  confirm() {
    if (!this._deleteUrl) return

    const confirmBtn = this.sheetTarget.querySelector("[data-action*='confirm']")
    if (confirmBtn) {
      confirmBtn.disabled = true
      confirmBtn.classList.add("is-submitting")
    }

    const form = document.createElement("form")
    form.method = "POST"
    form.action = this._deleteUrl
    form.style.display = "none"
    if (this._turboAction) form.dataset.turboAction = this._turboAction

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    if (csrf) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "authenticity_token"
      input.value = csrf
      form.appendChild(input)
    }

    const method = document.createElement("input")
    method.type = "hidden"
    method.name = "_method"
    method.value = "delete"
    form.appendChild(method)

    document.body.appendChild(form)
    form.requestSubmit()

    // Dismiss visually without sending bridge "show" — the page is navigating
    // away so the bottom nav will reappear naturally when the next page connects.
    this._isOpen = false
    this.backdropTarget.classList.add("opacity-0", "pointer-events-none")
    this.sheetTarget.classList.remove("translate-y-0")
    this.sheetTarget.classList.add("translate-y-full")
  }

  backdropClose(event) {
    if (event.target === this.backdropTarget) this.close()
  }

  // ── Swipe-to-dismiss (iOS-style drag from anywhere on the sheet) ─────────
  // Copied from voice_modal_controller. The sheet here is positioned with
  // Tailwind classes (translate-y-0 / translate-y-full), so the inline
  // transform is only used during the drag and cleared afterwards so the
  // classes stay authoritative.

  // Selector for elements that should consume the touch instead of starting a drag
  // (inputs/buttons/links + anything explicitly opted-in via data-no-drag).
  static DRAG_IGNORE_SELECTOR = "input, textarea, select, button, a, label, [contenteditable], [data-no-drag]"

  // A sheet is dragged by its handle, not by its contents. Binding the whole
  // surface meant a touch anywhere — on a mascot, on a paragraph, on the padding
  // between two rows — slid the sheet and moved the dashboard behind it, which
  // reads as the page coming loose rather than as a gesture.
  static DRAG_HANDLE_SELECTOR = "[data-drag-handle]"

  dragStart(event) {
    if (!this._isOpen) return
    // Ignore touches that originate on interactive controls so taps still work.
    if (event.target.closest(this.constructor.DRAG_IGNORE_SELECTOR)) {
      this._isDragging = false
      return
    }
    if (!event.target.closest(this.constructor.DRAG_HANDLE_SELECTOR)) {
      this._isDragging = false
      return
    }
    const touch = event.touches[0]
    this._dragStartY = touch.clientY
    this._dragCurrentY = touch.clientY
    this._dragStartX = touch.clientX
    this._dragCommitted = false   // becomes true once the gesture is clearly a vertical drag
    this._isDragging = true
    this.sheetTarget.style.transition = "none"
  }

  dragMove(event) {
    if (!this._isDragging) return

    const touch = event.touches[0]
    this._dragCurrentY = touch.clientY
    const deltaY = this._dragCurrentY - this._dragStartY
    const deltaX = touch.clientX - this._dragStartX

    // Decide direction once a small threshold is crossed. Horizontal-dominant
    // movement releases the gesture so things like text selection still work.
    if (!this._dragCommitted) {
      if (Math.abs(deltaY) < 6 && Math.abs(deltaX) < 6) return
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this._isDragging = false
        return
      }
      this._dragCommitted = true
    }

    if (deltaY <= 0) {
      this.sheetTarget.style.transform = "translateY(0)"
      return
    }

    // Prevent the page underneath from scrolling while we drag the sheet down.
    if (event.cancelable) event.preventDefault()

    this.sheetTarget.style.transform = `translateY(${deltaY}px)`
    const progress = Math.min(deltaY / (window.innerHeight * 0.3), 1)
    this.backdropTarget.style.opacity = 1 - progress * 0.8
  }

  dragEnd() {
    if (!this._isDragging) return
    const wasCommitted = this._dragCommitted
    this._isDragging = false
    this._dragCommitted = false

    if (!wasCommitted) {
      // Gesture never crossed the commit threshold (it was a tap or horizontal swipe).
      this.sheetTarget.style.transition = ""
      this.sheetTarget.style.transform = ""
      this.backdropTarget.style.opacity = ""
      return
    }

    const deltaY = this._dragCurrentY - this._dragStartY
    const velocity = deltaY / (window.innerHeight || 1)

    if (deltaY > 100 || velocity > 0.2) {
      this.close()
    } else {
      // Snap back — clear the inline transform so the translate-y-0 class wins.
      this.sheetTarget.style.transition = "transform 300ms cubic-bezier(0.2, 0.8, 0.25, 1)"
      this.sheetTarget.style.transform = ""
      this.backdropTarget.style.opacity = ""
    }
  }

  _showSheet() {
    this._isOpen = true
    this._bottomNavBridge?.hide()
    this.backdropTarget.classList.remove("opacity-0", "pointer-events-none")
    this.sheetTarget.style.transition = "transform 320ms cubic-bezier(0.32, 0.72, 0, 1)"
    this.sheetTarget.offsetHeight // reflow
    this.sheetTarget.classList.remove("translate-y-full")
    this.sheetTarget.classList.add("translate-y-0")
  }

  get _bottomNavBridge() {
    const el = document.querySelector("[data-controller~='bridge--bottom-nav']")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "bridge--bottom-nav")
  }
}
